// src/services/cloudflare-d1-sync.js — Cloudflare D1 Serverless Match Sync & Spectator Fetcher

const CLOUDFLARE_API_BASE = 'https://ffpl-scorer.pages.dev';
const EDIT_TOKEN_KEY_PREFIX = 'criccam_edit_token_';
let _lastSyncTimestamp = 0;

/**
 * Pushes a match snapshot to Cloudflare D1 Serverless Database + KV Store.
 * @param {Object} match - The match object from activeMatch or savedMatches
 * @returns {Promise<{ok: boolean, code?: string, error?: string, rateLimited?: boolean, updatedAt?: number}>}
 */
export async function pushMatchToCloudflareD1(match) {
  if (!match || !match.code) return { ok: false, error: "No match code" };

  // Rate limit: max 1 push every 1500ms
  const now = Date.now();
  if (now - _lastSyncTimestamp < 1500) {
    return { ok: false, rateLimited: true, retryAfter: 1500 - (now - _lastSyncTimestamp) };
  }
  _lastSyncTimestamp = now;

  try {
    const code = String(match.code).trim().toUpperCase();
    const storedToken = localStorage.getItem(EDIT_TOKEN_KEY_PREFIX + code) || "";
    
    const headers = {
      'Content-Type': 'application/json'
    };
    if (storedToken) {
      headers['x-match-edit-token'] = storedToken;
    }

    const response = await fetch(`${CLOUDFLARE_API_BASE}/matches?code=${code}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(match)
    });

    if (response.ok) {
      const data = await response.json();
      if (data.editToken) {
        localStorage.setItem(EDIT_TOKEN_KEY_PREFIX + code, data.editToken);
      }
      return { ok: true, code: data.code, updatedAt: data.updatedAt };
    } else {
      const errText = await response.text();
      return { ok: false, error: errText };
    }
  } catch (err) {
    console.warn('[CloudflareD1Sync] Push warning:', err);
    return { ok: false, error: err.message };
  }
}

/**
 * Fetches a live match snapshot from Cloudflare D1/KV for spectators.
 * @param {string} code - 6-character match code
 * @returns {Promise<{ok: boolean, match?: Object, error?: string, updatedAt?: number}>}
 */
export async function fetchMatchFromCloudflareD1(code) {
  if (!code) return { ok: false, error: "No code provided" };
  const cleanCode = String(code).trim().toUpperCase();

  try {
    const response = await fetch(`${CLOUDFLARE_API_BASE}/matches/${cleanCode}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    });

    if (response.ok) {
      const data = await response.json();
      const matchObj = data.match || data.record || data;
      return { ok: true, match: matchObj, updatedAt: data.updatedAt };
    } else {
      return { ok: false, error: `Match ${cleanCode} not found` };
    }
  } catch (err) {
    return { ok: false, error: err.message };
  }
}
